<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-[60] overflow-y-auto">
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="handleClose"></div>

      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative w-full max-w-lg bg-white rounded-2xl shadow-xl">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">绑定平台接收方</h3>
              <p class="text-sm text-slate-500 mt-0.5">上传合作协议后提交拉卡拉审核</p>
            </div>
            <button
              @click="handleClose"
              class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="p-6 space-y-5">
            <div class="bg-slate-50 rounded-xl p-4 space-y-3">
              <div class="flex items-center justify-between gap-4">
                <span class="text-sm text-slate-500">加盟商</span>
                <span class="text-sm font-medium text-slate-900 truncate">{{ franchiseeName }}</span>
              </div>
              <div class="flex items-center justify-between gap-4">
                <span class="text-sm text-slate-500">拉卡拉商户号</span>
                <span class="text-sm font-mono text-slate-900 truncate">{{ account?.merchantNo || '-' }}</span>
              </div>
              <div class="flex items-center justify-between gap-4">
                <span class="text-sm text-slate-500">平台接收方</span>
                <span class="text-sm font-mono text-slate-900 truncate">{{ platformReceiver?.receiverNo || '-' }}</span>
              </div>
              <div class="flex items-center justify-between gap-4">
                <span class="text-sm text-slate-500">绑定状态</span>
                <span class="status-badge" :class="bindStatusClass">{{ bindStatusText }}</span>
              </div>
            </div>

            <div v-if="!canSubmitBind" class="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800">
              当前加盟商分账账户状态不允许发起绑定。
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-slate-700">
                <span class="text-red-500">*</span>
                合作协议
              </label>
              <input
                ref="fileInputRef"
                type="file"
                accept=".jpg,.png,.pdf"
                class="hidden"
                @change="handleFileChange"
              >
              <div class="flex items-center gap-3">
                <button
                  type="button"
                  class="btn-secondary"
                  :disabled="uploading || submitting || !canSubmitBind"
                  @click="fileInputRef?.click()"
                >
                  {{ uploading ? '上传中...' : '选择文件' }}
                </button>
                <div class="min-w-0 flex-1 text-sm text-slate-600 truncate">
                  {{ selectedFileName || uploadedFile?.lakalaFileName || '未选择文件' }}
                </div>
              </div>
              <div v-if="uploadedFile?.lakalaFilePath" class="text-xs text-green-700">
                文件已上传，可以提交绑定申请
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
            <button @click="handleClose" class="btn-secondary">取消</button>
            <button
              @click="handleSubmit"
              :disabled="uploading || submitting || !canSubmitBind"
              class="btn-primary"
            >
              {{ submitting ? '提交中...' : '提交绑定申请' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import {
  bindFranchiseePlatformReceiver,
  getFranchiseePlatformLakalaReceiver,
  uploadLakalaFile
} from '@/api/commission'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  franchiseeId: {
    type: [Number, String],
    default: null
  },
  franchiseeInfo: {
    type: Object,
    default: null
  },
  account: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:visible', 'success'])

const fileInputRef = ref(null)
const selectedFileName = ref('')
const uploadedFile = ref(null)
const platformReceiver = ref(null)
const uploading = ref(false)
const submitting = ref(false)

const AGREEMENT_MAX_FILE_SIZE = 5 * 1024 * 1024
const AGREEMENT_ALLOWED_EXTENSIONS = ['jpg', 'png', 'pdf']
const AGREEMENT_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf']

const franchiseeName = computed(() => props.franchiseeInfo?.name || props.account?.payeeName || '-')
const currentBindStatus = computed(() => platformReceiver.value?.bindStatus ?? props.account?.bindStatus)
const canSubmitBind = computed(() => (
  props.account?.ledgerStatus === 'VALID'
    && !!platformReceiver.value?.receiverNo
    && currentBindStatus.value !== 0
    && currentBindStatus.value !== 1
    && currentBindStatus.value !== 3
))

const bindStatusText = computed(() => {
  const status = currentBindStatus.value
  if (status === 0) return '审核中'
  if (status === 1) return '已绑定'
  if (status === 2) return '绑定拒绝'
  if (status === 3) return '解绑审核中'
  if (status === 4) return '已解绑'
  if (status === 5) return '解绑拒绝'
  return '未绑定'
})

const bindStatusClass = computed(() => {
  const status = currentBindStatus.value
  if (status === 1) return 'bg-green-100 text-green-800'
  if (status === 0 || status === 3) return 'bg-amber-100 text-amber-800'
  if (status === 2 || status === 5) return 'bg-rose-100 text-rose-800'
  return 'bg-slate-100 text-slate-700'
})

const clearFileInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

const resetState = () => {
  selectedFileName.value = ''
  uploadedFile.value = null
  uploading.value = false
  submitting.value = false
  clearFileInput()
}

const validateAgreementFile = (file) => {
  const extension = file.name?.split('.').pop()?.toLowerCase() || ''
  const extensionAllowed = AGREEMENT_ALLOWED_EXTENSIONS.includes(extension)
  const typeAllowed = !file.type || AGREEMENT_ALLOWED_TYPES.includes(file.type)
  if (!extensionAllowed || !typeAllowed) {
    ElMessage.error('合作协议仅支持 JPG、PNG、PDF 格式')
    return false
  }
  if (file.size > AGREEMENT_MAX_FILE_SIZE) {
    ElMessage.error('合作协议文件大小不能超过 5M')
    return false
  }
  return true
}

const loadPlatformReceiver = async () => {
  if (!props.franchiseeId) {
    platformReceiver.value = null
    return
  }
  try {
    platformReceiver.value = await getFranchiseePlatformLakalaReceiver(props.franchiseeId)
  } catch (error) {
    platformReceiver.value = null
    console.error('查询平台接收方失败:', error)
  }
}

const handleClose = () => {
  resetState()
  emit('update:visible', false)
}

const handleFileChange = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return
  selectedFileName.value = ''
  uploadedFile.value = null
  if (!validateAgreementFile(file)) {
    clearFileInput()
    return
  }
  const targetTenantId = props.account?.payeeId || props.franchiseeId
  if (!targetTenantId) {
    ElMessage.error('缺少加盟商信息，无法上传合作协议')
    clearFileInput()
    return
  }
  selectedFileName.value = file.name
  uploading.value = true
  try {
    const formData = new FormData()
    formData.append('tenantId', String(targetTenantId))
    formData.append('attType', 'SPLIT_COOPERATION_FILE')
    formData.append('remark', '加盟商绑定平台分账接收方合作协议')
    formData.append('module', 'franchisee/platform-bind')
    formData.append('file', file)
    uploadedFile.value = await uploadLakalaFile(formData)
    ElMessage.success('合作协议上传成功')
  } catch (error) {
    console.error('上传合作协议失败:', error)
    ElMessage.error(error.message || '上传合作协议失败')
  } finally {
    uploading.value = false
  }
}

const handleSubmit = async () => {
  if (!uploadedFile.value?.lakalaFileName || !uploadedFile.value?.lakalaFilePath) {
    ElMessage.warning('请先上传合作协议')
    return
  }
  if (!props.franchiseeId) {
    ElMessage.warning('缺少加盟商ID')
    return
  }
  submitting.value = true
  try {
    await bindFranchiseePlatformReceiver(props.franchiseeId, {
      entrustFileName: uploadedFile.value.lakalaFileName,
      entrustFilePath: uploadedFile.value.lakalaFilePath
    })
    ElMessage.success('绑定申请已提交')
    emit('success')
    handleClose()
  } catch (error) {
    console.error('提交绑定申请失败:', error)
    ElMessage.error(error.message || '提交绑定申请失败')
  } finally {
    submitting.value = false
  }
}

watch(() => [props.visible, props.franchiseeId], ([visible]) => {
  if (visible) {
    resetState()
    loadPlatformReceiver()
  }
})
</script>
